/**
 * Safe toast wrapper around `sonner`.
 *
 * Round 38 — prevents the recurring "Objects are not valid as a React
 * child" (React minified error #31) crash that bites whenever a backend
 * returns a structured `detail` payload (FastAPI 422 array,
 * EMAIL_NOT_VERIFIED dict, horary_number_required dict, etc.) and
 * caller code writes `toast.error(err.response.data.detail)` or
 * `toast.error(err)` without thinking.
 *
 * Every message method (error / success / info / warning / message)
 * runs its first argument through `coerce` which guarantees a plain
 * string before sonner ever sees it. The original sonner API surface
 * (durations, IDs, custom etc.) is preserved.
 *
 * Use exactly like sonner:
 *   import { toast } from '@/lib/toast';
 *   toast.error(err);                                  // safe
 *   toast.error('Something went wrong');               // unchanged
 *   toast.error(err.response?.data?.detail);           // safe
 *   toast.success('Saved', { duration: 4000 });        // unchanged
 *   toast.info(detail.message);                        // safe (string)
 */
import { toast as sonnerToast } from 'sonner';
import { readErr } from './api';

function coerce(input, fallback) {
    if (input == null) return fallback;
    if (typeof input === 'string') return input;
    if (typeof input === 'number' || typeof input === 'boolean') return String(input);
    // Axios / Error-shaped objects → use the interceptor-attached
    // userMessage if present, else fall back to readErr.
    if (input && typeof input === 'object') {
        if (typeof input.userMessage === 'string' && input.userMessage.trim()) {
            return input.userMessage;
        }
        if (input.response || input.isAxiosError) {
            return readErr(input, fallback);
        }
        // FastAPI structured detail dict
        if (typeof input.message === 'string') return input.message;
        if (typeof input.msg === 'string') return input.msg;
        // FastAPI 422 array
        if (Array.isArray(input) && input.length) {
            const first = input[0];
            if (typeof first === 'string') return first;
            if (first && typeof first === 'object') {
                return first.msg || first.message || JSON.stringify(first);
            }
        }
        try { return JSON.stringify(input); } catch { /* fall through */ }
    }
    return fallback;
}

function wrap(method, fallback) {
    return (msg, opts) => sonnerToast[method](coerce(msg, fallback), opts);
}

export const toast = Object.assign(
    // Direct call: `toast('hello')` → sonnerToast('hello')
    (msg, opts) => sonnerToast(coerce(msg, ''), opts),
    {
        error:   wrap('error',   'Something went wrong'),
        success: wrap('success', 'Done'),
        info:    wrap('info',    ''),
        warning: wrap('warning', ''),
        message: wrap('message', ''),
        // Pass-throughs that don't take string content as their first arg.
        loading:  (msg, opts) => sonnerToast.loading(coerce(msg, 'Loading…'), opts),
        promise:  sonnerToast.promise.bind(sonnerToast),
        custom:   sonnerToast.custom.bind(sonnerToast),
        dismiss:  sonnerToast.dismiss.bind(sonnerToast),
    },
);
